"use client";

import { useBreakPoints } from "@/hooks/styles/useBreakpoints";
import { useDetectAdBlock } from "@/hooks/useDetectAdBlocker";
import { __DEV__ } from "@/lib/constants";
import FreestarAdSlot from "@freestar/pubfig-adslot-react-component";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { useEffect } from "react";
import { FREESTAR_PUBLISHER } from "../constants";
import { EmptyTrackingDiv } from "./FreestarAdSlot";

/**
 * Starts the Freestar Sliding Video Ad (Lower Right Corner)
 * @param lower - boolean: This is to be used on pages where there is no footer, so the ad unit is placed lower on the page
 * @returns
 */
const AdUnit = ({ lower = false }: { lower?: boolean }) => {
    const AD_UNIT_NAME = lower ? "FreeStarVideoAdContainer_slider_nf" : "FreeStarVideoAdContainer_slider";

    const { smDown } = useBreakPoints();

    useEffect(() => {
        if (smDown) {
            return;
        }

        if (typeof window !== "undefined") {
            // biome-ignore lint/suspicious/noConsole:
            console.log("Freestar Video Ad Loaded");
            window.freestar = window.freestar || {};
            window.freestar.queue = window.freestar.queue || [];

            window.freestar.queue.push(() => {
                window.freestar.newVideo(AD_UNIT_NAME);
            });
        }

        return () => {
            if (typeof window !== "undefined") {
                // biome-ignore lint/suspicious/noConsole:
                console.log("Freestar Video Ad Removed");
                window.freestar = window.freestar || {};
                window.freestar.queue = window.freestar.queue || [];

                window.freestar.queue.push(() => {
                    window.freestar.deleteVideo(AD_UNIT_NAME);
                });
            }
        };
    }, []);

    if (smDown) {
        return <EmptyTrackingDiv />;
    }

    return (
        <>
            {/* <AdShieldRecovery /> */}
            <FreestarAdSlot
                publisher={FREESTAR_PUBLISHER}
                placementName="FreeStarVideoAdContainer_slider"
                slotId="FreeStarVideoAdContainer_slider"
                // targeting={targeting}
                channel="custom_channel"
                onNewAdSlotsHook={placementName => {
                    // biome-ignore lint/suspicious/noConsole:
                    console.log("creating ad", placementName);
                }}
                onDeleteAdSlotsHook={placementName => {
                    // biome-ignore lint/suspicious/noConsole:
                    console.log("destroying ad", placementName);
                }}
                onAdRefreshHook={placementName => {
                    // biome-ignore lint/suspicious/noConsole:
                    console.log("refreshing ad", placementName);
                }}
            />
        </>
    );
};

const SlidingVideoAd = ({ isEnabled = true, lower = false }: { isEnabled?: boolean; lower?: boolean }) => {
    const { loginInProgress, user, isSubscriptionActive, isUnder13, canShowAds } = useCurrentUser();
    const adBlockDetected = useDetectAdBlock();
    const { smDown } = useBreakPoints();

    if (isSubscriptionActive || isUnder13 || !user || loginInProgress || !canShowAds) {
        return <EmptyTrackingDiv />;
    }

    if (smDown) {
        return <EmptyTrackingDiv />;
    }

    if (!isEnabled) {
        return <EmptyTrackingDiv />;
    }

    if (__DEV__ || adBlockDetected) {
        return null;
    }

    return <AdUnit lower={lower} />;
};

export default SlidingVideoAd;
